import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Home, Users, Briefcase, Clock, FileText, Bell, Settings, LogOut, Menu, X,
  ChevronRight, ChevronDown, Plus, CheckCircle, AlertCircle, MapPin, Calendar, User,
  ClipboardList, BarChart3, Play, Square, RefreshCw, Search, Filter, Eye,
  Truck, Camera, Upload, Image, Wrench, Shield, FileImage, Trash2, Save,
  Building2, Sliders, Key, MessageSquare, Send, Car, Fuel, Video, HelpCircle,
  ChevronLeft, Download, Edit3, Check, ArrowLeft, Edit2, UserCheck, Archive, Receipt, BookOpen
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { Badge, Button, Card, Input, Modal, Select, Spinner, Textarea, FileUploadInput } from "../../shared/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const DailyRosterSection = ({ jobId, jobTitle }) => {
  const [roster, setRoster] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [newAssignment, setNewAssignment] = useState({ user_id: "", role_for_day: "", notes: "" });

  useEffect(() => {
    fetchData();
  }, [jobId]);

  const fetchData = async () => {
    try {
      const [rosterRes, usersRes] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}/roster`),
        axios.get(`${API}/users`)
      ]);
      setRoster(rosterRes.data);
      setUsers(usersRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const addAssignment = async () => {
    if (!newAssignment.user_id || !selectedDate) return;
    try {
      await axios.post(`${API}/jobs/${jobId}/roster`, {
        job_id: jobId,
        user_id: newAssignment.user_id,
        date: selectedDate,
        role_for_day: newAssignment.role_for_day,
        notes: newAssignment.notes
      });
      setNewAssignment({ user_id: "", role_for_day: "", notes: "" });
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to add assignment");
    }
  };

  const deleteAssignment = async (assignmentId) => {
    if (!window.confirm("Remove this assignment?")) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}/roster/${assignmentId}`);
      fetchData();
    } catch (e) {
      alert("Failed to delete assignment");
    }
  };

  // Group roster by date
  const rosterByDate = roster.reduce((acc, item) => {
    if (!acc[item.date]) acc[item.date] = [];
    acc[item.date].push(item);
    return acc;
  }, {});

  if (loading) return <div className="flex justify-center py-8"><Spinner /></div>;

  return (
    <div className="space-y-6">
      {/* Add Assignment Form */}
      <Card className="bg-green-50 border-green-200">
        <h4 className="font-bold uppercase text-green-800 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Add Daily Assignment
        </h4>
        <div className="grid md:grid-cols-2 gap-4">
          <Input
            label="Date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
          <Select
            label="Worker"
            value={newAssignment.user_id}
            onChange={(e) => setNewAssignment({...newAssignment, user_id: e.target.value})}
            options={[
              { value: "", label: "Select worker..." },
              ...users.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name} (${u.role.replace('_', ' ')})` }))
            ]}
          />
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <Select
            label="Role for Day"
            value={newAssignment.role_for_day}
            onChange={(e) => setNewAssignment({...newAssignment, role_for_day: e.target.value})}
            options={[
              { value: "", label: "Same as usual" },
              { value: "manager", label: "Manager" },
              { value: "supervisor", label: "Supervisor" },
              { value: "crane_operator", label: "Crane Operator" },
              { value: "rigger", label: "Rigger" },
              { value: "labourer", label: "Labourer" },
              { value: "spotter", label: "Spotter" },
              { value: "dogman", label: "Dogman" }
            ]}
          />
          <Input
            label="Notes"
            value={newAssignment.notes}
            onChange={(e) => setNewAssignment({...newAssignment, notes: e.target.value})}
            placeholder="e.g., Morning shift only"
          />
        </div>
        <Button type="button" variant="secondary" size="sm" className="mt-4" onClick={addAssignment}>
          <Plus className="w-4 h-4 mr-2" />
          Add to Roster
        </Button>
      </Card>

      {/* Roster by Date */}
      <div className="space-y-4">
        <h4 className="font-bold uppercase text-slate-600">Scheduled Workers</h4>
        {Object.keys(rosterByDate).sort().map(date => (
          <Card key={date}>
            <h5 className="font-bold text-lg mb-3 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-500" />
              {new Date(date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short' })}
            </h5>
            <div className="space-y-2">
              {rosterByDate[date].map(assignment => (
                <div key={assignment.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-semibold">{assignment.user_name}</p>
                    <p className="text-sm text-slate-500">
                      {assignment.role_for_day?.replace('_', ' ') || assignment.user_role?.replace('_', ' ')}
                      {assignment.notes && ` • ${assignment.notes}`}
                    </p>
                  </div>
                  <button onClick={() => deleteAssignment(assignment.id)} className="text-red-500 hover:text-red-700 p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        ))}
        {Object.keys(rosterByDate).length === 0 && (
          <p className="text-center text-slate-500 py-8">No workers scheduled yet. Add assignments above.</p>
        )}
      </div>
    </div>
  );
};

// Signing Documents View Component - For documents requiring signatures

export default DailyRosterSection;
